/**
 * shared/rubrics/deckChecklist.js — Công cụ #11: pitch deck 10 slide.
 *
 * Chạy trên engine readinessScore, không cần engine riêng. Đây là lý do engine
 * đó được viết để chạy rubric bất kỳ ngay từ đầu.
 *
 * Đường dẫn dùng không gian tên `input.*` — dữ liệu nằm ở
 * ventures/{id}/inputs/pitch-deck, không phải trong profile.
 *
 * Dạng dữ liệu công cụ gửi vào:
 *   {
 *     slides: { PROBLEM: { filled: true, notes: '…' }, … },
 *     slideCount: 12,
 *     hasAppendix: true,
 *     askIsSpecific: true,
 *     hasContact: true,
 *     showsLeverage: true,
 *     showsMoat: true,
 *   }
 *
 * Version 2 (11/2026): thêm trục "Đòn bẩy và hào phòng thủ" (Technology và
 * Trenches trong khung 5T), hai mục founder hay bỏ sót nhất. Trọng số: mười
 * slide 65, đòn bẩy và hào phòng thủ 15, cách trình bày 20.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO KHÔNG PHẢI MỘT DANH SÁCH GẠCH ĐẦU DÒNG
 *
 * Danh sách "10 slide cần có" thì tìm đâu cũng ra. Giá trị nằm ở phần
 * `whyItMatters` — nói cho founder biết nhà đầu tư THỰC SỰ tìm gì trên slide
 * đó, vì phần lớn người làm deck lần đầu điền đúng tiêu đề nhưng sai nội dung.
 * ─────────────────────────────────────────────────────────────────────────
 */

const slide = (key, label, points, whyItMatters, howToFix) => ({
  key,
  label,
  points,
  rule: { path: `input.slides.${key}.filled`, op: 'isTrue' },
  whyItMatters,
  howToFix,
});

export const DECK_CHECKLIST = {
  rubricId: 'deck-checklist-preseed',
  version: 2,
  status: 'PUBLISHED',
  label: 'Pitch deck 10 slide cho vòng angel và pre-seed',
  appliesTo: { stages: ['IDEA', 'PROTOTYPE', 'ANGEL', 'PRESEED'] },

  axes: [
    {
      key: 'NOI_DUNG',
      label: 'Mười slide cốt lõi',
      weight: 65,
      criteria: [
        slide('PROBLEM', 'Vấn đề', 8,
          'Nhà đầu tư giai đoạn đầu mua vấn đề trước, giải pháp sau. Họ tìm dấu hiệu bạn đã nói chuyện thật với người đang chịu vấn đề đó, không phải suy đoán từ xa.',
          'Mô tả bằng ngôn ngữ khách hàng dùng, kèm họ đang xoay xở bằng cách nào. Một câu trích nguyên văn từ khách hàng thật có sức nặng hơn cả slide.'),

        slide('SOLUTION', 'Giải pháp', 8,
          'Điều họ tìm không phải danh sách tính năng, mà là vì sao cách của bạn giải quyết đúng vấn đề vừa nêu ở slide trước.',
          'Cho thấy sản phẩm đang chạy: ảnh chụp màn hình thật, không phải hình minh hoạ. Bỏ hết tính từ như "đột phá", "tối ưu".'),

        slide('WHY_NOW', 'Vì sao là lúc này', 6,
          'Vấn đề này chắc đã tồn tại nhiều năm. Câu hỏi luôn được hỏi là vì sao bây giờ mới làm được, và vì sao chưa ai làm.',
          'Chỉ ra thứ vừa thay đổi: quy định mới, hành vi người dùng đổi, công nghệ vừa đủ rẻ, hoặc một thị trường vừa mở.'),

        slide('MARKET', 'Quy mô thị trường', 7,
          'Không ai tin con số TAM lấy từ báo cáo ngành. Cái họ đọc là bạn suy ra con số đó bằng cách nào.',
          'Dựng từ dưới lên bằng công cụ quy mô thị trường: bao nhiêu khách khả thi × mỗi khách trả bao nhiêu một năm. Ghi rõ từng giả định.'),

        slide('TRACTION', 'Bằng chứng', 9,
          'Ở pre-seed chưa cần doanh thu, nhưng phải có gì đó chứng minh người ngoài thực sự muốn thứ này. Đây là slide quyết định nhiều nhất.',
          'Thư cam kết, triển khai thử, đơn đặt trước, danh sách chờ. Thứ tự ưu tiên là có ai trả tiền chưa. Nếu chưa có gì, hãy trung thực và nói kế hoạch lấy bằng chứng.'),

        slide('BUSINESS_MODEL', 'Kiếm tiền thế nào', 7,
          'Ai trả tiền, trả bao nhiêu, trả theo chu kỳ nào. Nghe đơn giản nhưng rất nhiều deck pre-seed bỏ trống slide này.',
          'Nêu giá cụ thể và cơ sở của mức giá đó. Nếu chưa thu tiền, nói rõ mức giá dự kiến và cách bạn kiểm chứng nó.'),

        slide('COMPETITION', 'Cạnh tranh', 6,
          'Trả lời "không có đối thủ" gần như luôn bị hiểu là chưa tìm hiểu kỹ. Khách hàng đang giải quyết vấn đề bằng cách nào đó, kể cả bằng Excel.',
          'Liệt kê cả cách làm thủ công đang tồn tại. Nói rõ bạn khác ở đâu và vì sao khác biệt đó khó sao chép.'),

        slide('TEAM', 'Đội ngũ', 9,
          'Ở vòng đầu, đây là thứ được đọc kỹ nhất. Nhà đầu tư đặt cược vào người khi chưa có gì khác để đánh giá.',
          'Với mỗi người: vì sao chính họ làm được việc này. Ghi rõ ai toàn thời gian. Nếu có mảng còn thiếu, nói ra và nói kế hoạch bù.'),

        slide('FINANCIALS', 'Kế hoạch tài chính', 7,
          'Không ai tin dự báo ba năm ở pre-seed. Cái họ xem là bạn có hiểu cấu trúc chi phí của chính mình không.',
          'Đưa mô hình 18–24 tháng dựng từ dưới lên. Số nhỏ mà có cơ sở tốt hơn số lớn mà không giải thích được.'),

        slide('ASK', 'Gọi bao nhiêu và làm gì', 8,
          'Slide bị bỏ quên nhiều nhất, và là slide nhà đầu tư cần để quyết định có nói tiếp hay không.',
          'Số tiền cụ thể, hình thức (SAFE hay cổ phần), số tháng nó nuôi được, và cột mốc nào đạt được nhờ nó.'),
      ],
    },

    {
      key: 'LOI_THE',
      label: 'Đòn bẩy và hào phòng thủ',
      weight: 15,
      criteria: [
        {
          key: 'DON_BAY',
          label: 'Cho thấy lớn nhanh được mà chi phí không phình theo',
          points: 8,
          rule: { path: 'input.showsLeverage', op: 'isTrue' },
          whyItMatters:
            'Quỹ cần mỗi khoản đầu tư có khả năng trả lại cả quỹ. Deck phải cho thấy khi khách gấp 10 lần, chi phí phục vụ không gấp 10 lần theo.',
          howToFix:
            'Ở slide giải pháp hoặc mô hình kinh doanh, ghi chi phí phục vụ một khách hôm nay và dự kiến khi quy mô tăng, kèm phần việc đã tự động hoá.',
        },
        {
          key: 'HAO_PHONG_THU',
          label: 'Nói rõ lợi thế khó sao chép, kèm bằng chứng',
          points: 8,
          rule: { path: 'input.showsMoat', op: 'isTrue' },
          whyItMatters:
            'Câu hỏi luôn đến sau slide cạnh tranh: nếu bạn thành công, điều gì ngăn công ty lớn làm y hệt. "Chúng tôi đi trước" không phải câu trả lời.',
          howToFix:
            'Gọi tên một lợi thế đang xây: dữ liệu riêng, hiệu ứng mạng, chi phí chuyển đổi, giấy phép, kênh độc quyền. Thêm một con số chứng minh, ví dụ tỷ lệ khách ở lại.',
        },
      ],
    },

    {
      key: 'CHAT_LUONG',
      label: 'Cách trình bày',
      weight: 20,
      criteria: [
        {
          key: 'DO_DAI',
          label: 'Deck dưới 15 slide',
          points: 8,
          rule: { path: 'input.slideCount', op: 'lte', value: 15 },
          partialRule: { path: 'input.slideCount', op: 'lte', value: 20 },
          partialPoints: 4,
          whyItMatters:
            'Deck gửi qua email thường được xem trong 3–4 phút. Trên 15 slide là gần như chắc chắn có phần không ai đọc.',
          howToFix:
            'Giữ 10 slide chính, đẩy phần còn lại xuống phụ lục. Phụ lục dài bao nhiêu cũng được, vì nó chỉ mở ra khi có người hỏi.',
        },
        {
          key: 'PHU_LUC',
          label: 'Có phụ lục cho câu hỏi sâu',
          points: 5,
          rule: { path: 'input.hasAppendix', op: 'isTrue' },
          whyItMatters:
            'Phụ lục là nơi để chi tiết cohort, cấu trúc giá, phân tích cạnh tranh: những thứ làm deck chính rối nhưng lại rất hữu ích khi bị hỏi sâu.',
          howToFix:
            'Thêm vài slide sau slide gọi vốn: chi tiết tài chính, lộ trình sản phẩm, bảng cổ phần, hồ sơ từng người trong nhóm.',
        },
        {
          key: 'ASK_CU_THE',
          label: 'Số tiền gọi là con số cụ thể',
          points: 7,
          rule: { path: 'input.askIsSpecific', op: 'isTrue' },
          whyItMatters:
            'Viết "gọi 3–10 tỷ" cho thấy bạn chưa tính kỹ cần bao nhiêu. Nhà đầu tư đọc khoảng rộng như một dấu hiệu thiếu chuẩn bị.',
          howToFix:
            'Dùng công cụ gọi bao nhiêu để ra một con số, rồi giải thích nó nuôi được bao nhiêu tháng và tới cột mốc nào.',
        },
        {
          key: 'LIEN_HE',
          label: 'Có thông tin liên hệ trên slide cuối',
          points: 5,
          rule: { path: 'input.hasContact', op: 'isTrue' },
          whyItMatters:
            'Deck được chuyển tay giữa các nhà đầu tư. Người thứ ba nhận được file mà không biết liên hệ ai thì cơ hội dừng ở đó.',
          howToFix:
            'Slide cuối: tên, email, số điện thoại, website. Đặt luôn trong phần chân của mọi slide cũng không thừa.',
        },
      ],
    },
  ],

  flags: [
    {
      code: 'THIEU_SLIDE_DOI_NGU',
      severity: 'HIGH',
      rule: { path: 'input.slides.TEAM.filled', op: 'isTrue' },
      dao: true,
      message: 'Deck chưa có slide đội ngũ.',
      fix: 'Ở vòng đầu đây là slide được đọc kỹ nhất. Thiếu nó thì phần lớn deck bị bỏ qua ngay từ vòng sàng lọc.',
    },
    {
      code: 'THIEU_SLIDE_GOI_VON',
      severity: 'HIGH',
      rule: { path: 'input.slides.ASK.filled', op: 'isTrue' },
      dao: true,
      message: 'Deck chưa có slide gọi bao nhiêu và dùng vào việc gì.',
      fix: 'Không có slide này, nhà đầu tư không biết bạn muốn gì và thường sẽ không hỏi lại.',
    },
    {
      code: 'THIEU_BANG_CHUNG',
      severity: 'MEDIUM',
      rule: { path: 'input.slides.TRACTION.filled', op: 'isTrue' },
      dao: true,
      message: 'Deck chưa có slide bằng chứng.',
      fix: 'Kể cả khi chưa có doanh thu, vẫn phải có gì đó cho thấy người ngoài quan tâm: thư cam kết, pilot, danh sách chờ.',
    },
    {
      code: 'DECK_QUA_DAI',
      severity: 'MEDIUM',
      rule: { path: 'input.slideCount', op: 'gt', value: 20 },
      message: 'Deck dài trên 20 slide.',
      fix: 'Chuyển phần lớn xuống phụ lục. Deck chính nên đọc xong trong 4 phút.',
    },
  ],

  bands: [
    { min: 85, key: 'SAN_SANG_GUI', label: 'Sẵn sàng gửi cho nhà đầu tư' },
    { min: 65, key: 'GAN_XONG', label: 'Gần xong, còn vài slide' },
    { min: 40, key: 'DANG_LAM', label: 'Đang làm dở' },
    { min: 0, key: 'MOI_BAT_DAU', label: 'Mới bắt đầu' },
  ],
};

export default DECK_CHECKLIST;
